import { useLazyQuery } from "@apollo/client";
import { useNavigate } from "react-router-dom";

import { RVIcon, RVIconSizeOptions, PaginationResponse } from "@regal-voice/ui";
import { SpotlightRoutingItem } from "Components/elements/SpotlightSearch/Components/Routing/SpotlightRoutingItem";
import { useOrchestrationPathing } from "Pages/orchestration/useOrchestrationPathing";
import { getPaginatedCampaigns } from "Services/marketing-api/campaigns/queries";

type Campaign = {
    id: string;
    name: string;
    description?: string;
};

type GetCampaignsResponse = {
    getCampaigns: PaginationResponse<Campaign>;
};

export const useGetSearchCampaigns = (onCallback: () => void) => {
    const navigate = useNavigate();
    const { campaignsBasePath } = useOrchestrationPathing();
    const [getCampaigns, { loading }] = useLazyQuery<GetCampaignsResponse>(getPaginatedCampaigns);

    const handleGetCampaigns = async (searchValue: string) => {
        if (!searchValue.trim()) {
            return [];
        }

        const campaigns = await getCampaigns({
            variables: {
                queryParams: { filterBy: [{ key: "name", value: searchValue, operator: "contains" }] },
            },
        });

        const campaignsData = campaigns.data?.getCampaigns.items.map((item) => ({
            label: (
                <SpotlightRoutingItem
                    icon={<RVIcon icon={"campaign"} size={RVIconSizeOptions.SMALL} />}
                    name={item.name}
                    description={item.description || "Campaign"}
                    searchValue={searchValue}
                    route={`${campaignsBasePath}/${item.id}`}
                    onCallback={() => {
                        onCallback();
                        navigate(`${campaignsBasePath}/${item.id}`);
                    }}
                />
            ),
            value: item.id,
        }));

        if (!campaignsData?.length || !searchValue) {
            return [];
        }

        return [
            {
                label: "Campaigns",
                options: campaignsData,
            },
        ];
    };

    return {
        getCampaigns: handleGetCampaigns,
        loading,
    };
};
